/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 *
 * Singleton for the google maps api. Used to ensure that the google maps api is loaded once when needed.
 * Also, the instance of google maps should be the same in order to avoid the creation of multiple different maps
 * in the application or unnecessary reloads.
 */
import chroma from 'chroma-js';
import { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import React from 'react';
import { bpsToUnitString } from '../../../utils/DataRateUtils';

type UniqueFeatureBox = { color: number[]; bps: number };
type UniqueFeatureBoxes = { [bps: number]: { color: number[]; bps: number } };

export type HeatmapLegendProps = {
  type: 'vertical-gradient' | 'boxes';
  featureCollection: FeatureCollection<MultiPolygon>;
  baseColor: any;
  blendColor?: any;
  enableBlend?: boolean;
  blendOpacity?: number;
  sortDirection: 'ascending' | 'descending';
  linkType: 'rl' | 'fl';
  boxLimit: number;
  labelTextColor: string;
  boxBorderColor: string;
};

const HeatmapLegend = (props: HeatmapLegendProps) => {
  const {
    type,
    featureCollection,
    baseColor,
    blendColor,
    blendOpacity,
    enableBlend,
    linkType,
    boxLimit,
    boxBorderColor,
    labelTextColor,
    sortDirection,
  } = props;
  if (!chroma.valid(baseColor instanceof Function ? baseColor() : baseColor)) {
    console.warn('Heatmap Legend: Invalid value supplied for property "color"');
    return <></>;
  }
  const filteredfeatureCollection = {
    ...featureCollection,
    features: featureCollection.features.filter(
      (feature: Feature<MultiPolygon | Polygon>) =>
        (feature.properties || {}).rlDataRateBps || (feature.properties || {}).flDataRateBps,
    ),
  };

  const lowestBps = filteredfeatureCollection.features.reduce(
    (lowestRate, currentFeature: Feature<MultiPolygon>) =>
      (linkType === 'rl' ? currentFeature.properties?.rlDataRateBps : currentFeature.properties?.flDataRateBps) <
      lowestRate
        ? linkType === 'rl'
          ? currentFeature.properties?.rlDataRateBps
          : currentFeature.properties?.flDataRateBps
        : lowestRate,
    Infinity,
  );
  const greatestBps = filteredfeatureCollection.features.reduce(
    (greatestRate, currentFeature: Feature<MultiPolygon>) =>
      (linkType === 'rl' ? currentFeature.properties?.rlDataRateBps : currentFeature.properties?.flDataRateBps) >
      greatestRate
        ? linkType === 'rl'
          ? currentFeature.properties?.rlDataRateBps
          : currentFeature.properties?.flDataRateBps
        : greatestRate,
    -Infinity,
  );
  if (greatestBps === -Infinity || lowestBps === Infinity) {
    return <></>;
  }

  const uniqueFeatureBoxes: UniqueFeatureBoxes = (filteredfeatureCollection.features || []).reduce(
    (accum: UniqueFeatureBoxes, current: Feature<Polygon | MultiPolygon>) => {
      const bps =
        linkType === 'rl' ? (current.properties || {}).rlDataRateBps : (current.properties || {}).flDataRateBps;
      const color = baseColor instanceof Function ? baseColor() : baseColor;
      const gradientStopColor = chroma(color).alpha(1).darken(4).saturate(3);
      const colorScale = chroma.scale([chroma(color).alpha(1), gradientStopColor]).domain([lowestBps, greatestBps]);

      let mixedColor = colorScale(bps);

      if (enableBlend) {
        if (chroma.valid(blendColor)) {
          // Cannot get around the folowing ts-ignores cause of the .domain call above. Works with just scale, which operates on a domain of [0,1] by default, but
          // the custom domain is needed for the logic to work properly. ts ignores are from the types not being compatable but it works at runtime. Spent hours looking into
          // a work around but none was found. Documentation is available at https://gka.github.io/chroma.js/
          // @ts-ignore
          const mixedToAverageMapBackgroundColorScale = chroma.scale([chroma(blendColor).alpha(1), mixedColor]);
          // Same as from above
          // @ts-ignore
          mixedColor = mixedToAverageMapBackgroundColorScale(blendOpacity);
        } else {
          console.warn('Heatmap Legend: Invalid value supplied for property "blendColor"');
        }
      }

      return {
        ...accum,
        // Same as from above
        // @ts-ignore
        [bps]: { color: mixedColor.rgb(), bps },
      };
    },
    {} as UniqueFeatureBoxes,
  );
  const excedesBoxLimit = Object.keys(uniqueFeatureBoxes || {}).length > boxLimit;
  const color = baseColor instanceof Function ? baseColor() : baseColor;
  let startColor = chroma(color).alpha(1);
  let stopColor = chroma(color).alpha(1).darken(4).saturate(3);
  if (enableBlend) {
    const mixedToAverageMapBackgroundColorScaleStart = chroma.scale([chroma(blendColor).alpha(1), startColor]);
    startColor = mixedToAverageMapBackgroundColorScaleStart(blendOpacity);
    const mixedToAverageMapBackgroundColorScaleStop = chroma.scale([chroma(blendColor).alpha(1), stopColor]);
    stopColor = mixedToAverageMapBackgroundColorScaleStop(blendOpacity);
  }
  if (type === 'boxes' && !excedesBoxLimit) {
    return Object.values(uniqueFeatureBoxes)
      .sort((a: UniqueFeatureBox, b: UniqueFeatureBox) =>
        a.bps < b.bps ? (sortDirection === 'ascending' ? 1 : -1) : sortDirection === 'ascending' ? -1 : 1,
      )
      .map((featureBox: UniqueFeatureBox) => (
        <div
          key={featureBox.bps}
          style={{ display: 'flex', alignItems: 'center', paddingBottom: '4px', flexDirection: 'row' }}
        >
          <div
            style={{
              border: `1px solid ${boxBorderColor}`,
              width: '24px',
              height: '24px',
              backgroundColor: chroma.rgb(featureBox.color[0], featureBox.color[1], featureBox.color[2]).hex(),
            }}
          ></div>
          <div style={{ color: labelTextColor, paddingLeft: '8px' }}>
            {featureBox.bps ? bpsToUnitString(featureBox.bps, 0, 2) : '--'}
          </div>
        </div>
      ));
  } else if (type === 'vertical-gradient' || (type === 'boxes' && excedesBoxLimit)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div
          style={{
            height: boxLimit * 30 - 6,
            width: 24,
            backgroundImage: `linear-gradient(${stopColor}, ${startColor})`,
            border: `1px solid ${boxBorderColor}`,
          }}
        ></div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            marginLeft: '8px',
            color: 'rgba(255,255,255,0.8)',
            height: boxLimit * 30,
            paddingTop: 4,
            paddingBottom: 8,
            paddingLeft: 0,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ color: labelTextColor }}>{bpsToUnitString(greatestBps, 0, 2)}</div>
          <div style={{ color: labelTextColor }}>{bpsToUnitString((greatestBps + lowestBps) * 0.75, 0, 2)}</div>
          <div style={{ color: labelTextColor }}>{bpsToUnitString((greatestBps + lowestBps) * 0.5, 0, 2)}</div>
          <div style={{ color: labelTextColor }}>{bpsToUnitString((greatestBps + lowestBps) * 0.25, 0, 2)}</div>
          <div style={{ color: labelTextColor }}>{bpsToUnitString(lowestBps, 0, 2)}</div>
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};

/**
 * This component represents a GeoJSON overlay on a map. This will most likely be used to put
 * a beam on the map.
 */
// @ts-ignore
const _HeatmapLegend = React.forwardRef(HeatmapLegend);
export { _HeatmapLegend as HeatmapLegend };
